import { useEffect, useId, useRef, useState } from 'react'
import { api, type Member } from '../lib/api'

/** Who accepted: a box that finds a member as he is typed -- part of a name,
 *  a unit, or the digits of a number -- and logs him on Enter or a click.
 *
 *  AND THE WHOLE ROSTER TO SCROLL, the customer's own correction (22 Sep
 *  2026): "I can search for any member, but I still want to have the drop
 *  down option and scroll down the whole list." With nothing typed, the box
 *  opens on every active member in unit order, from the roster this screen
 *  already holds; typing narrows it through the server's search.
 *
 *  THE SEARCH IS THE SERVER'S. The roster this screen holds has no numbers
 *  for a dispatcher (`PICKER_FIELDS`), so a number can only be matched
 *  there, and the answer says "phone" rather than which phone. Eight rows at
 *  most, units first when the typing starts a unit. Members already going
 *  are left out, as the old list left them out. */
export function MemberPicker({ roster, exclude, onPick, label, placeholder }: {
  roster: Member[]
  exclude: string[]
  /** The member's id, and how he is named -- for a list that draws him
   *  before anything is saved. */
  onPick: (memberId: string, who: string) => void
  /** What the box is for, when it is not logging who accepted on a live call:
   *  who went on a call logged afterwards, or one added in a correction (078). */
  label?: string
  placeholder?: string
}) {
  /** ITS OWN IDS, so two of these on one screen never point at each other's
   *  list. */
  const listId = useId()
  const [term, setTerm] = useState('')
  /** Whether the list is open under the box: on focus, on the arrow, and
   *  closed on Escape, on a pick, or on leaving the box. */
  const [open, setOpen] = useState(false)
  const [found, setFound] = useState<Awaited<ReturnType<typeof api.memberPick>>>([])
  /** The term the rows in `found` answer. "Nobody matches" is said only
   *  once a search for THIS term has come back, not during the round trip. */
  const [answered, setAnswered] = useState('')
  const [at, setAt] = useState(0)
  const attempt = useRef(0)

  useEffect(() => {
    const typed = term.trim()
    // EVERY CHANGE RETIRES THE SEARCH IN FLIGHT, an emptied box included:
    // a reply landing after Escape or a pick must not refill the list.
    const mine = ++attempt.current
    if (!typed) { setFound([]); setAnswered(''); setAt(0); return }
    const t = setTimeout(() => {
      api.memberPick(typed)
        .then((rows) => {
          if (mine !== attempt.current) return
          setFound(rows)
          setAnswered(typed)
          setAt(0)
        })
        .catch(() => { if (mine === attempt.current) { setFound([]); setAnswered(typed) } })
    }, 120)
    return () => clearTimeout(t)
  }, [term])

  // NOTHING TYPED: the whole roster, active, in the order people count units
  // (MT-3, MT-9, MT-20), names without a unit at the end. Typed: the server's
  // matches. Members already going are left out AT RENDER either way, so one
  // logged a moment ago disappears from a list fetched before he was.
  const whole = term.trim()
    ? found
    : [...roster]
        .filter((m) => m.active)
        .sort((a, b) =>
          (a.callsign || '\uffff').localeCompare(b.callsign || '\uffff', undefined,
            { numeric: true, sensitivity: 'base' })
          || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
        .map((m) => ({
          id: m.id, callsign: m.callsign, display_name: m.display_name, label: m.label,
          on_duty: m.on_duty, has_radio: m.has_radio, matched_on: null as null,
        }))
  const shown = open ? whole.filter((r) => !exclude.includes(r.id)) : []
  const cursor = Math.min(at, Math.max(shown.length - 1, 0))

  function take(id: string) {
    const row = whole.find((r) => r.id === id)
    attempt.current++
    setTerm('')
    setFound([])
    setAnswered('')
    setOpen(false)
    onPick(id, row ? (row.callsign || row.display_name || row.label) : 'A member')
  }

  const what: Record<string, string> = { unit: 'unit', name: 'name', phone: 'their number' }

  return (
    <div className="picker">
      <div className="pickrow">
        <input
          className="orgpick"
          style={{ width: '100%', maxWidth: 'none' }}
          value={term}
          placeholder={placeholder ?? 'Log who accepted — pick from the list, or type a unit, a name or the last digits of a number'}
          aria-label={label ?? 'Log who accepted'}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={shown.length > 0}
          aria-controls={listId}
          aria-activedescendant={shown.length ? `${listId}-${shown[cursor].id}` : undefined}
          onFocus={() => setOpen(true)}
          // A CLICK OPENS IT TOO: after a pick the box keeps the cursor, so a
          // second member could not be chosen without leaving and coming back.
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => { setTerm(e.target.value); setOpen(true) }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setAt(Math.min(cursor + 1, shown.length - 1)) }
            if (e.key === 'ArrowUp') { e.preventDefault(); setAt(Math.max(cursor - 1, 0)) }
            if (e.key === 'Enter' && !e.nativeEvent.isComposing && shown[cursor]) {
              e.preventDefault(); take(shown[cursor].id)
            }
            if (e.key === 'Escape') { setTerm(''); setOpen(false) }
          }}
        />
        {/* THE ARROW: the dropdown he asked for. Mouse-down rather than click,
            with the default prevented, so the box keeps focus and the list
            does not close on the way to opening. */}
        <button type="button" className="btn sm pickarrow" aria-label="Show the whole list"
                onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o) }}>
          ▾
        </button>
      </div>
      {answered && answered === term.trim() && open && shown.length === 0 && (
        <div className="footnote" style={{ margin: '6px 0 0' }}>Nobody on the roster matches that.</div>
      )}
      {shown.length > 0 && (
        <div className={`picklist ${term.trim() ? '' : 'long'}`} role="listbox" id={listId}
             onMouseDown={(e) => e.preventDefault()}>
          {shown.map((m, i) => (
            <button type="button" role="option" aria-selected={i === cursor}
                    id={`${listId}-${m.id}`}
                    className={`pick ${i === cursor ? 'on' : ''}`} key={m.id}
                    onMouseEnter={() => setAt(i)}
                    onClick={() => take(m.id)}>
              {m.callsign && <span className="mono cs">{m.callsign}</span>}
              <span className="nm">{m.display_name || (m.callsign ? '' : m.label)}</span>
              <span className="lbl">
                {m.on_duty ? 'on duty' : 'off duty'}
                {m.matched_on && m.matched_on !== 'unit' && m.matched_on !== 'name'
                  ? ` · by ${what[m.matched_on]}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
